// src/pages/resource/ResourceDirectoryPage.tsx
// Resource Directory — the global, cross-entity home for the resource
// module. Replaces the generated CRUD scaffold (ResourceListPage/
// DetailPage/CreatePage/EditPage/SettingsPage), which modeled "a resource"
// as an editable record — Resource is a read-only projection over other
// services' entities, so there is nothing to create/edit here except the
// one thing this service does write directly: external URLs.
//
// Per-entity linking (LinkResources/UnlinkResources/ListLinks/GetLinkStats/
// UpdateResourceLink) stays in the right rail next to the entity being
// linked — see layouts/RightRail.tsx and features/resources/
// ResourceManagePanel.tsx. This page is for cross-tenant discovery and the
// small amount of admin/reference data (external URL registry, resource
// type catalog) that has no natural entity to live next to.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge } from '@react/components';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import { resourceApi } from '@core/api';
import { searchResources, listExternalResources, listResourceTypes, getExternalResourceByURL } from '@/features/resources/api';
import { entityToRoute } from '@/features/resources/logic';
import { errMessage, fmtDateTime } from '@/features/resources/format';
import type { Resource, ExternalResource, ResourceType } from '@core/types/resource';

type Tab = 'search' | 'external' | 'types';

export const ResourceDirectoryPage = () => {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('search');

  return (
    <div className="space-y-4 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('resource.directory.title')}</h1>

      <div className="flex border-b border-border">
        {(['search', 'external', 'types'] as Tab[]).map((tb) => (
          <button
            key={tb}
            onClick={() => setTab(tb)}
            className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === tb ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`resource.directory.tab.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'search' && <SearchTab navigate={navigate} />}
      {tab === 'external' && <ExternalTab locale={locale} />}
      {tab === 'types' && <TypesTab />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Search tab — SearchResources (free text) + ListRecentResources (default view)
// ---------------------------------------------------------------------------

function SearchTab({ navigate }: { navigate: (path: string) => void }) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Resource[]>([]);
  const [recent, setRecent] = useState<Resource[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoadingRecent(true);
    resourceStore.ListRecentResources({ pagination: { limit: 30, cursor: '', direction: 'forward' }, sort: [], view: 0 } as any)
      .then(() => setRecent(resourceStore.items))
      .catch(() => {})
      .finally(() => setIsLoadingRecent(false));
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) { setResults([]); return; }
    setIsSearching(true);
    setError(null);
    searchResources(query.trim(), 30)
      .then((res) => setResults(res.resources?.items ?? []))
      .catch((e) => setError(errMessage(e, t('resource.directory.searchError'))))
      .finally(() => setIsSearching(false));
  };

  const openResource = (r: Resource) => {
    const route = entityToRoute({ type_name: r.entity?.type_name, type_id: r.entity?.type_id, id: r.entity?.id });
    if (route) navigate(route);
  };

  const list = query.trim() ? results : recent;
  const loading = query.trim() ? isSearching : isLoadingRecent;

  return (
    <div className="space-y-3">
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('resource.directory.searchPlaceholder')}
          className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit" size="sm" variant="secondary">{t('common.search')}</Button>
      </form>
      {!query.trim() && (
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('resource.directory.recent')}</p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('resource.directory.empty')}</p>
      ) : (
        <ul className="space-y-1">
          {list.map((r) => (
            <li key={r.entity?.id ?? r.urn}>
              <button
                onClick={() => openResource(r)}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <span className="min-w-0 flex-1 truncate text-foreground">{r.title || r.urn}</span>
                {r.subtitle && <span className="truncate text-xs text-muted-foreground">{r.subtitle}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// External URLs tab — CreateExternalResource, GetExternalResourceByURL,
// ListExternalResources, RefreshExternalResource
// ---------------------------------------------------------------------------

function ExternalTab({ locale }: { locale: string }) {
  const { t } = useI18n();
  const [items, setItems] = useState<ExternalResource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (searchTerm?: string) => {
    setIsLoading(true);
    listExternalResources(searchTerm)
      .then((res) => setItems(res.resources?.resources ?? []))
      .catch((e) => setError(errMessage(e, t('resource.directory.externalLoadError'))))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = newUrl.trim();
    if (!url) return;
    setIsAdding(true);
    setError(null);
    try {
      const existing = await getExternalResourceByURL(url).catch(() => null);
      if (existing?.resource) {
        setItems((prev) => [existing.resource as ExternalResource, ...prev.filter((r) => r.entity?.id !== existing.resource?.entity?.id)]);
      } else {
        const created = await resourceApi.CreateExternalResource({ url });
        if (created.resource) setItems((prev) => [created.resource as ExternalResource, ...prev]);
      }
      setNewUrl('');
    } catch (e) {
      setError(errMessage(e, t('resource.directory.externalAddError')));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRefresh = async (r: ExternalResource) => {
    const id = r.entity?.id;
    if (!id) return;
    setRefreshingId(id);
    try {
      const res = await resourceApi.RefreshExternalResource(id, {});
      if (res.resource) setItems((prev) => prev.map((it) => (it.entity?.id === id ? res.resource as ExternalResource : it)));
    } catch (e) {
      setError(errMessage(e, t('resource.directory.externalRefreshError')));
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={handleAdd} className="flex gap-2">
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="https://…"
          className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit" size="sm" isLoading={isAdding} disabled={!newUrl.trim()}>{t('resource.directory.externalAdd')}</Button>
      </form>
      <form onSubmit={(e) => { e.preventDefault(); load(search.trim() || undefined); }} className="flex gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit" size="sm" variant="secondary">{t('common.search')}</Button>
      </form>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('resource.directory.empty')}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((r) => (
            <li key={r.entity?.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{r.title || r.url}</p>
                <p className="truncate text-xs text-muted-foreground">{r.domain} · {fmtDateTime(r.last_fetched_at, locale)}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleRefresh(r)} isLoading={refreshingId === r.entity?.id}>
                {t('resource.directory.externalRefresh')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types tab — ListResourceTypes, GetResourceType
// ---------------------------------------------------------------------------

function TypesTab() {
  const { t } = useI18n();
  const [types, setTypes] = useState<ResourceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<ResourceType | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    listResourceTypes()
      .then((res) => setTypes(res.types?.types ?? []))
      .catch((e) => setError(errMessage(e, t('resource.directory.typesLoadError'))))
      .finally(() => setIsLoading(false));
  }, [t]);

  const openType = (name: string) => {
    setIsLoadingDetail(true);
    resourceStore.GetResourceType(name)
      .then((res) => setSelected(res.type ?? null))
      .catch(() => setSelected(null))
      .finally(() => setIsLoadingDetail(false));
  };

  return (
    <div className="flex gap-4">
      <div className="flex-1 space-y-2">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : types.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('resource.directory.empty')}</p>
        ) : (
          <ul className="space-y-1">
            {types.map((rt) => (
              <li key={rt.name}>
                <button
                  onClick={() => openType(rt.name)}
                  className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <span className="text-foreground">{rt.display_name || rt.name}</span>
                  <Badge variant="secondary" size="sm">{rt.category}</Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {(selected || isLoadingDetail) && (
        <div className="w-72 flex-shrink-0 rounded-md border border-border p-3">
          {isLoadingDetail ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : selected && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">{selected.display_name || selected.name}</h3>
              <p className="text-xs text-muted-foreground">{selected.description}</p>
              <p className="text-xs text-muted-foreground">{t('resource.directory.typeProvider')}: {selected.provider_slug || '—'}</p>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('resource.directory.typeFields')}</p>
                <ul className="mt-1 space-y-0.5">
                  {(selected.fields?.fields ?? []).map((f: any) => (
                    <li key={f.name} className="text-xs text-foreground">{f.name}</li>
                  ))}
                  {(selected.fields?.fields ?? []).length === 0 && <li className="text-xs text-muted-foreground">—</li>}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ResourceDirectoryPage;
